import React from 'react';
import {
  alpha,
  Box,
  darken,
  Button,
  Chip,
  IconButton,
  lighten,
  Paper,
  Stack,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Casino, Close, OpenInNew, QueryStats, SportsScore, Star, StarBorder, TouchApp } from '@mui/icons-material';
import { DriverStreak, SimDriverResult, TrackRankPill as TrackRankPillData } from '../services/api';
import { lastName, useChartTokens } from './charts/chartKit';
import { FinishOddsChart } from './charts/FinishOddsChart';
import { FieldRow, mapQuadrant, ordText, pctText, useFieldRows } from './charts/FieldCharts';
import { StreakPill, StreakRaceList } from './StreakPill';
import { getCellColor } from '../utils/cellColors';
import { ratingAsPosition, vivid } from '../utils/simColors';
import { TrackRankPill } from './TrackRankPill';

const panelColors = (theme: Theme) => ({
  cardBg:
    theme.palette.mode === 'dark' ? lighten(theme.palette.background.paper, 0.05) : theme.palette.background.paper,
  bodyBg: alpha(theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.background.default, 0.5),
});

/** The Tiers / Boom or bust sidebar before a driver is picked. */
export const SimDriverPanelEmpty: React.FC<{ view: 'tiers' | 'boom' }> = ({ view }) => {
  const theme = useTheme();
  const { cardBg } = panelColors(theme);
  return (
    <Paper sx={{ p: 3, bgcolor: cardBg, textAlign: 'center' }}>
      <TouchApp sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Select a driver to see more</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: 12.5 }}>
        {view === 'tiers'
          ? 'Click a driver in the chart to see where they sit in their tier, how close the next tier is, and their last races here and at similar tracks.'
          : 'Click a driver in the chart for their boom and bust chances, where they finish in our simulations, and how often they really boomed or busted here and at similar tracks.'}
      </Typography>
    </Paper>
  );
};

/** A small heading that says where a group of numbers comes from (our simulations, or real races). */
const GroupLabel: React.FC<{ icon: React.ReactNode; title: string; sub: string }> = ({ icon, title, sub }) => (
  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75, mt: 1.5, '&:first-of-type': { mt: 0 } }}>
    <Box sx={{ display: 'flex', color: 'text.secondary' }}>{icon}</Box>
    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase' }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>· {sub}</Typography>
  </Stack>
);

/** A small labeled number tile. */
const Tile: React.FC<{ label: string; value: React.ReactNode; note?: React.ReactNode; color?: string }> = ({
  label,
  value,
  note,
  color,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: 1.25,
      borderRadius: 2,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'divider',
    }}
  >
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'text.secondary' }} noWrap>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize:
          typeof value === 'string' && value.length > 8 ? 14 : typeof value === 'string' && value.length > 6 ? 16 : 20,
        lineHeight: '30px',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        color,
      }}
      noWrap
    >
      {value}
    </Typography>
    {note && (
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>
        {note}
      </Typography>
    )}
  </Box>
);

const Section: React.FC<{ title: string; sub?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  sub,
  children,
}) => (
  <Box sx={{ mt: 2.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
    {sub && (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5, mb: 1 }}>
        {sub}
      </Typography>
    )}
    {children}
  </Box>
);

/** The driver's tier-mates' ranges (the Tiers chart, zoomed to one tier); click one to switch to them. */
const TierMates: React.FC<{ rows: FieldRow[]; me: FieldRow; onSelect: (driver: string) => void }> = ({
  rows,
  me,
  onSelect,
}) => {
  const theme = useTheme();
  const tokens = useChartTokens();
  const [hover, setHover] = React.useState<string | null>(null);
  const mates = rows.filter((r) => r.tier === me.tier);
  const W = 320;
  const L = 84;
  const ROW = 20;
  const lo = Math.min(...mates.map((r) => r.q[0]));
  const hi = Math.max(...mates.map((r) => r.q[4]));
  const X = (p: number) => L + ((p - lo) / Math.max(1, hi - lo)) * (W - L - 8);
  return (
    <svg
      viewBox={`0 0 ${W} ${mates.length * ROW + 18}`}
      width="100%"
      style={{ display: 'block' }}
      onMouseLeave={() => setHover(null)}
    >
      {mates.map((r, i) => {
        const y = i * ROW + ROW / 2;
        const picked = r.d.driver === me.d.driver;
        // like the main chart: the picked or hovered driver's bar and dot get thicker
        const on = picked || r.d.driver === hover;
        return (
          <g
            key={r.d.driver}
            onClick={() => onSelect(r.d.driver)}
            onMouseEnter={() => setHover(r.d.driver)}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={0}
              y={i * ROW}
              width={W}
              height={ROW}
              rx={4}
              fill={
                picked ? alpha(r.tierColor, 0.18) : r.d.driver === hover ? theme.palette.action.hover : 'transparent'
              }
            />
            <text x={L - 8} y={y + 4} textAnchor="end" fontSize={11.5} fontWeight={on ? 700 : 500} fill={tokens.text}>
              {lastName(r.d.driver)}
            </text>
            <line
              x1={X(r.q[0])}
              x2={X(r.q[4])}
              y1={y}
              y2={y}
              stroke={r.tierColor}
              strokeOpacity={0.55}
              strokeWidth={1.5}
            />
            <line
              x1={X(r.q[1])}
              x2={X(r.q[3])}
              y1={y}
              y2={y}
              stroke={r.tierColor}
              strokeWidth={on ? 9 : 6}
              strokeLinecap="round"
            />
            <circle
              cx={X(r.d.avgFinish)}
              cy={y}
              r={on ? 4.5 : 3.5}
              fill={tokens.surface}
              stroke={tokens.text}
              strokeWidth={1.5}
            />
          </g>
        );
      })}
      <text x={L} y={mates.length * ROW + 14} fontSize={10.5} fill={theme.palette.text.secondary}>
        {ordText(lo)}
      </text>
      <text x={W - 8} y={mates.length * ROW + 14} textAnchor="end" fontSize={10.5} fill={theme.palette.text.secondary}>
        {ordText(hi)}
      </text>
    </svg>
  );
};

/**
 * The driver panel: the Tiers and Boom or bust tabs' sidebar (in place of the bet tools; a bottom sheet on phones).
 * The same header on both tabs (name, pills, star, Driver details); the body follows the tab: Tiers shows where the
 * driver sits in their tier and how close the neighboring tiers are; Boom or bust shows their boom and bust chances,
 * where they land on the map, and how often they really boomed or busted here and at similar tracks.
 */
export const SimDriverPanel: React.FC<{
  view: 'tiers' | 'boom';
  d: SimDriverResult;
  field: SimDriverResult[];
  nSims: number;
  site: string;
  trackPill?: TrackRankPillData;
  pillSince: number;
  /** The driver's last races here and at similar tracks; `kind` is set when they have a streak pill. */
  recent?: DriverStreak;
  streakTracks: string[];
  streakSince: number;
  favorite: boolean;
  onToggleFavorite: () => void;
  onSelect: (driver: string) => void;
  onClose: () => void;
  onOpenDetails: () => void;
}> = ({
  view,
  d,
  field,
  nSims,
  site,
  trackPill,
  pillSince,
  recent,
  streakTracks,
  streakSince,
  favorite,
  onToggleFavorite,
  onSelect,
  onClose,
  onOpenDetails,
}) => {
  const streak = recent?.kind ? recent : undefined;
  const theme = useTheme();
  const rows = useFieldRows(field);
  const me = rows.find((r) => r.d.driver === d.driver);
  const { cardBg, bodyBg } = panelColors(theme);
  const races = recent?.races ?? [];
  const realAvg = races.length ? races.reduce((a, r) => a + r.finish, 0) / races.length : null;
  const similar = streakTracks.filter((t) => t !== site);
  const tracksNote = `${site}${similar.length ? ` and ${similar.join(', ')}` : ''}${streakSince ? `, since ${streakSince}` : ''}`;

  // ---- Tiers ----
  const tierBody = () => {
    if (!me) return null;
    const nTiers = Math.max(...rows.map((r) => r.tier)) + 1;
    const mates = rows.filter((r) => r.tier === me.tier);
    const rank = mates.indexOf(me) + 1;
    const prev = rows.filter((r) => r.tier === me.tier - 1);
    const next = rows.filter((r) => r.tier === me.tier + 1);
    const best = mates[0].d.avgFinish;
    const worst = mates[mates.length - 1].d.avgFinish;
    const mean = (vals: number[]) => {
      const v = vals.filter((x) => x > 0); // 0 = missing in the loop data
      return v.length ? v.reduce((a, x) => a + x, 0) / v.length : null;
    };
    const realRunning = mean(races.map((r) => r.running));
    const realRating = mean(races.map((r) => r.rating));
    // Numbers colored like the table: the red-yellow-green position scale (1st green, 40th red). Driver rating maps
    // onto it from 130 (green) down to 40 (red).
    // As text on a white panel the scale needs more darkening than the table's filled cells get.
    const posColor = (v: number) => {
      const c = vivid(getCellColor(v, 'position'));
      return theme.palette.mode === 'light' ? darken(c, 0.4) : c;
    };
    const ratingColor = (v: number) => posColor(ratingAsPosition(v));
    return (
      <>
        <GroupLabel icon={<Casino sx={{ fontSize: 15 }} />} title="Our simulations" sub="10,000 races" />
        <Stack direction="row" spacing={1}>
          <Tile label="TIER" value={`${me.tier + 1}`} note={`of ${nTiers}`} color={me.tierColor} />
          <Tile
            label="AVG FINISH"
            value={d.avgFinish.toFixed(1)}
            note={`${ordText(rank)} of ${mates.length} in tier`}
            color={posColor(d.avgFinish)}
          />
          <Tile
            label="USUALLY"
            value={`${ordText(me.q[1])}–${ordText(me.q[3])}`}
            note="half of finishes"
            color={posColor(me.q[2])}
          />
        </Stack>
        {races.length > 0 && realAvg !== null && (
          <>
            <GroupLabel
              icon={<SportsScore sx={{ fontSize: 16 }} />}
              title="Real races"
              sub={`last ${races.length} here and at similar tracks`}
            />
            <Stack direction="row" spacing={1}>
              <Tile
                label="AVG FINISH"
                value={realAvg.toFixed(1)}
                note={`${recent!.top10} top 10${recent!.top10 === 1 ? '' : 's'}`}
                color={posColor(realAvg)}
              />
              <Tile
                label="RUNNING"
                value={realRunning !== null ? realRunning.toFixed(1) : '–'}
                note="avg position"
                color={realRunning !== null ? posColor(realRunning) : undefined}
              />
              <Tile
                label="RATING"
                value={realRating !== null ? realRating.toFixed(1) : '–'}
                note="driver rating"
                color={realRating !== null ? ratingColor(realRating) : undefined}
              />
            </Stack>
          </>
        )}
        <Section
          title={`Tier ${me.tier + 1}`}
          sub={
            <>
              Simulated average finishes {best.toFixed(1)} to {worst.toFixed(1)}.
              {prev.length > 0 &&
                ` Tier ${me.tier} ends at ${prev[prev.length - 1].d.avgFinish.toFixed(1)} (${(best - prev[prev.length - 1].d.avgFinish).toFixed(1)} places up).`}
              {next.length > 0 &&
                ` Tier ${me.tier + 2} starts at ${next[0].d.avgFinish.toFixed(1)} (${(next[0].d.avgFinish - worst).toFixed(1)} places back).`}
            </>
          }
        >
          <TierMates rows={rows} me={me} onSelect={onSelect} />
        </Section>
        {races.length > 0 && (
          <Section
            title={`Last ${races.length} here and at similar tracks`}
            sub={
              <>
                {recent!.top5} top 5{recent!.top5 === 1 ? '' : 's'} · {recent!.top10} top 10
                {recent!.top10 === 1 ? '' : 's'} · {tracksNote}
              </>
            }
          >
            <StreakRaceList races={races} site={site} kind={recent?.kind} showRunning />
          </Section>
        )}
      </>
    );
  };

  // ---- Boom or bust ----
  const boomBody = () => {
    if (!me) return null;
    const boomRank = [...rows].sort((a, b) => b.d.top5Prob - a.d.top5Prob).indexOf(me) + 1;
    const bustRank = [...rows].sort((a, b) => a.bust - b.bust).indexOf(me) + 1;
    const quad = mapQuadrant(rows, d.driver);
    const booms = races.filter((r) => r.finish <= 5).length;
    const busts = races.filter((r) => r.finish >= 21).length;
    return (
      <>
        <Stack direction="row" spacing={1}>
          <Tile
            label="BOOM"
            value={pctText(d.top5Prob)}
            note={`${ordText(boomRank)} most`}
            color={theme.palette.success.main}
          />
          <Tile
            label="BUST"
            value={pctText(me.bust)}
            note={`${ordText(bustRank)} least`}
            color={theme.palette.error.main}
          />
          <Tile label="WIN" value={pctText(d.winProb)} />
        </Stack>
        {quad && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <Chip size="small" label={quad.label} color={quad.tone} variant="outlined" sx={{ fontWeight: 700 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
              {quad.note}
            </Typography>
          </Stack>
        )}
        <Section title="Where they finish">
          <FinishOddsChart drivers={[d]} field={field} nSims={nSims} />
        </Section>
        {races.length > 0 && (
          <Section title={`Real booms and busts: last ${races.length} here and at similar tracks`} sub={tracksNote}>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <Tile
                label="BOOMS"
                value={`${booms} of ${races.length}`}
                note={`sims: about ${(d.top5Prob * races.length).toFixed(1)}`}
                color={theme.palette.success.main}
              />
              <Tile
                label="BUSTS"
                value={`${busts} of ${races.length}`}
                note={`sims: about ${(me.bust * races.length).toFixed(1)}`}
                color={theme.palette.error.main}
              />
            </Stack>
            <StreakRaceList races={races} site={site} kind={recent?.kind} />
          </Section>
        )}
      </>
    );
  };

  return (
    <Paper sx={{ p: 0, bgcolor: cardBg }}>
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1.5,
          bgcolor: 'background.paper',
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{d.driver}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
              Proj {d.projectedRank} · avg finish {d.avgFinish.toFixed(1)}
              {me ? ` · Tier ${me.tier + 1}` : ''}
            </Typography>
          </Box>
          <Tooltip title={favorite ? 'Unstar (removes from Compare)' : 'Star (adds to Compare)'}>
            <IconButton size="small" onClick={onToggleFavorite} aria-label={favorite ? 'Unstar driver' : 'Star driver'}>
              {favorite ? (
                <Star fontSize="small" sx={{ color: 'warning.main' }} />
              ) : (
                <StarBorder fontSize="small" sx={{ color: 'text.secondary' }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear">
            <IconButton size="small" onClick={onClose} aria-label="Clear the selected driver">
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {(trackPill || streak) && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
            {trackPill && <TrackRankPill pill={trackPill} site={site} since={pillSince} />}
            {streak && <StreakPill streak={streak} site={site} tracks={streakTracks} since={streakSince} />}
          </Stack>
        )}
      </Box>
      <Box sx={{ bgcolor: bodyBg, p: 2, borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
        {/* Filled, with a pop-out icon, so it reads as a button that opens the driver details window. */}
        <Button
          variant="contained"
          fullWidth
          onClick={onOpenDetails}
          startIcon={<QueryStats />}
          endIcon={<OpenInNew fontSize="small" />}
          sx={{ mb: 2, fontWeight: 700 }}
        >
          Driver details
        </Button>
        {view === 'tiers' ? tierBody() : boomBody()}
      </Box>
    </Paper>
  );
};
